/**
 * DragGhost — el fantasma translúcid d'una reserva mentre es mou de fila (runbook 7, F3).
 *
 * Es pinta al mateix lloc que l'original (mateix `left` i amplada de `calculateReservationPosition`,
 * a mig dia, D4: la columna no canvia mai) i
 * `useReservationDrag` li escriu el `translateY` directament a l'`style`, sense repintar la graella.
 * Per això aquí **no** es posa cap `transform` a l'`style`: React el trepitjaria a cada render.
 * Verd si la fila de destí és lliure, vermell si xoca. Les vores tallades de la card (`clipStart`/
 * `clipEnd`) treuen l'arrodoniment del marc pel mateix costat. Fa el mateix lloc vertical que la card
 * original (`slotStyle`, F7: px segons l'alçada de la fila), encara que la fila de destí tingui
 * cancel·lades en franja: és un fantasma, no s'hi queda.
 */

import type { Ref } from 'react';
import { cn } from '@/lib/utils';
import { ReservationCell, type ReservationCellData } from '../ReservationCell';
import { clipRoundingClasses, type RowSlotStyle } from './row-layout';
import { useCalendarTheme } from '../calendar-theme';
import { stayCornerStyle } from '../calendar-radius';

export interface DragGhostProps {
  data: ReservationCellData;
  left: number;
  width: number;
  /** Lloc vertical de la card original (`rowSlotStyle`). */
  slotStyle: RowSlotStyle;
  collision: boolean;
  ghostRef: Ref<HTMLDivElement>;
}

export function DragGhost({ data, left, width, slotStyle, collision, ghostRef }: DragGhostProps) {
  const { cardRadius, rightRadius } = useCalendarTheme();
  return (
    <div
      ref={ghostRef}
      aria-hidden="true"
      data-testid="drag-ghost"
      className={cn(
        'pointer-events-none absolute z-[21] rounded-lg opacity-80 shadow-xl ring-2',
        clipRoundingClasses(data),
        collision ? 'ring-red-500' : 'ring-green-500',
      )}
      style={{ left, width, ...slotStyle, ...stayCornerStyle(cardRadius, rightRadius, data) }}
    >
      {/* La mateixa card: franja i llums (la mida, de la graella; l'amplada, la del fantasma). */}
      <ReservationCell data={{ ...data, cardWidth: width }} className="h-full" />
    </div>
  );
}

export default DragGhost;
